import { create } from 'zustand';

import { buildExport, serialiseExport } from '../../data/export';
import { metaRepo } from '../../data/repositories';
import { azureBlobProvider, type RefreshableProvider } from './azureBlob';
import { backupCredentials, canSeal } from './credentials';
import { fromBase64, sealBackup, serialiseEnvelope } from './envelope';
import { speechService } from '../speech/SpeechService';
import type { Meta } from '../types';

/**
 * Automatic backup (4.6).
 *
 * The rule that shapes every line here: **this must never be noticeable.** A backup that interrupts, blocks
 * or warns is worse than no backup, because the thing it interrupts is somebody being heard. So it runs late
 * after launch, skips while speech is in flight, fails silently, and reports only into `meta` where a
 * caregiver can look for it deliberately.
 *
 * ── Silent failure is a decision, not laziness ──
 *
 * 4.6 asks for it explicitly, and the reason is worth keeping: the person cannot fix a failed upload, and a
 * modal in front of them would be the app taking their screen to tell somebody else about a problem. What
 * replaces the modal is the status in care mode, escalating after seven consecutive failures — enough that a
 * bad week is visible and a bad train journey is not.
 *
 * ── More often when storage is fragile, not less ──
 *
 * 4.6 poses the question and answers it: when `persistGranted === false` the local database is evictable,
 * which is precisely when the remote copy matters most. So the interval *shortens*.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/** Normal cadence: once a day is plenty for something that changes at the speed of conversation. */
export const NORMAL_INTERVAL_MS = DAY_MS;
/** When `persistGranted === false`. Four times a day, because the local copy could vanish. */
export const FRAGILE_INTERVAL_MS = 6 * 60 * 60 * 1000;

/**
 * How long after launch the first attempt is even considered.
 *
 * Not a performance trick — the seal is milliseconds, since the stored key means no PBKDF2. It is about
 * ordering: the seconds after launch are when somebody is most likely to be starting a sentence, and the
 * app has better things to do with them than serialise a database.
 */
export const STARTUP_DELAY_MS = 20_000;

/** 4.6's escalation threshold. */
export const ESCALATE_AFTER_FAILURES = 7;

export interface BackupStatus {
  lastBackupAt: string | null;
  lastAttemptAt: string | null;
  lastError: string | null;
  failures: number;
  /** False until a caregiver has minted the credentials. */
  configured: boolean;
  /** True while an attempt is in flight, so care mode can say so rather than looking dead. */
  running: boolean;
}

interface BackupState extends BackupStatus {
  hydrate: () => Promise<void>;
  /** Force an attempt regardless of the interval — the caregiver's "jetzt sichern". */
  backUpNow: () => Promise<boolean>;
}

const initial: BackupStatus = {
  lastBackupAt: null,
  lastAttemptAt: null,
  lastError: null,
  failures: 0,
  configured: false,
  running: false,
};

export const useBackup = create<BackupState>((set) => ({
  ...initial,

  hydrate: async () => {
    const meta = await metaRepo.get();
    await azureBlobProvider.refresh();
    set(statusFrom(meta, azureBlobProvider.isConfigured()));
  },

  backUpNow: async () => {
    set({ running: true });
    try {
      return await attempt({ provider: azureBlobProvider, force: true });
    } finally {
      set({ running: false });
    }
  },
}));

function statusFrom(meta: Meta, configured: boolean): BackupStatus {
  return {
    lastBackupAt: meta.lastBackupAt ?? null,
    lastAttemptAt: meta.lastBackupAttemptAt ?? null,
    lastError: meta.lastBackupError ?? null,
    failures: meta.backupFailures ?? 0,
    configured,
    running: false,
  };
}

export function intervalFor(meta: Pick<Meta, 'persistGranted'>): number {
  return meta.persistGranted === false ? FRAGILE_INTERVAL_MS : NORMAL_INTERVAL_MS;
}

/**
 * Whether an automatic attempt is due.
 *
 * Pure, so the cadence can be reasoned about without a clock or a database — the alternative is a rule
 * nobody can test and everybody has to trust.
 */
export function isDue(meta: Meta, nowMs: number): boolean {
  const last = meta.lastBackupAt === undefined ? null : Date.parse(meta.lastBackupAt);
  // Never backed up: due immediately. This is the case that matters most and it is easy to get wrong by
  // treating a missing timestamp as "recent".
  if (last === null || !Number.isFinite(last)) return true;
  return nowMs - last >= intervalFor(meta);
}

export interface AttemptOptions {
  provider: RefreshableProvider;
  /** Skip the interval check. The caregiver asked. */
  force?: boolean;
  now?: () => number;
  isSpeaking?: () => boolean;
}

/**
 * One attempt. Resolves `false` when nothing was uploaded, for any reason, and **never rejects**.
 *
 * Never rejecting is the contract that makes the caller safe: this is invoked from a timer at startup, where
 * an unhandled rejection would be an unhandled error in front of somebody trying to talk.
 */
export async function attempt(options: AttemptOptions): Promise<boolean> {
  const now = options.now ?? Date.now;
  const speaking = options.isSpeaking ?? (() => speechService.isSpeaking());

  try {
    await options.provider.refresh();
    // Not configured is not a failure: a device whose caregiver has not set backup up yet has nothing
    // wrong with it, and counting it as a failure would trip the escalation on a device that never asked
    // for remote backup at all.
    if (!options.provider.isConfigured()) return false;

    const credentials = await backupCredentials();
    if (!credentials) return false;

    // A device that has typed its key off the card but not yet restored can *read* the cloud and cannot seal
    // for it — the replacement-device state (C57). Nothing to do, and emphatically not a failure: counting it
    // would escalate a warning at somebody who is in the middle of fixing the problem.
    if (!canSeal(credentials) || !credentials.key || !credentials.salt) return false;

    const meta = await metaRepo.get();
    if (!options.force && !isDue(meta, now())) return false;

    // Deferred rather than cancelled: the next launch will try again, and speaking is never made to wait.
    if (speaking()) return false;

    const document_ = await buildExport();
    const plaintext = await serialiseExport(document_).text();

    const envelope = await sealBackup({
      plaintext,
      key: credentials.key,
      salt: fromBase64(credentials.salt),
      schemaVersion: document_.schemaVersion,
      createdAt: document_.exportedAt,
    });

    await options.provider.push(serialiseEnvelope(envelope), {
      schemaVersion: document_.schemaVersion,
      createdAt: document_.exportedAt,
    });

    const at = new Date(now()).toISOString();
    await metaRepo.update({
      lastBackupAt: at,
      lastBackupAttemptAt: at,
      lastBackupError: null,
      backupFailures: 0,
    });
    useBackup.setState({
      lastBackupAt: at,
      lastAttemptAt: at,
      lastError: null,
      failures: 0,
      configured: true,
    });

    return true;
  } catch (err) {
    await recordFailure(err, now);
    return false;
  }
}

async function recordFailure(err: unknown, now: () => number): Promise<void> {
  // Deliberately quiet: console only, no UI. The status in care mode is where a human finds out.
  console.warn('[backup] attempt failed', err);

  try {
    const meta = await metaRepo.get();
    const failures = (meta.backupFailures ?? 0) + 1;
    const at = new Date(now()).toISOString();
    const message = err instanceof Error ? err.message : String(err);

    await metaRepo.update({
      lastBackupAttemptAt: at,
      lastBackupError: message,
      backupFailures: failures,
    });
    useBackup.setState({ lastAttemptAt: at, lastError: message, failures });
  } catch (nested) {
    // The database itself is unhappy. Nothing left to do but not make it worse.
    console.error('[backup] could not record the failure either', nested);
  }
}

let timer: ReturnType<typeof setTimeout> | null = null;

/**
 * Start the daily attempt. Called once from `main.tsx`, after render.
 *
 * One shot per launch rather than a repeating interval. This device gets closed and reopened many times a
 * day, so launches are a more natural clock than wall time — and a timer that keeps firing in a backgrounded
 * PWA is a timer that fires at unpredictable moments on iPadOS anyway.
 */
export function startBackup(): void {
  void useBackup.getState().hydrate();

  if (timer !== null) clearTimeout(timer);
  timer = setTimeout(() => {
    void attempt({ provider: azureBlobProvider });
  }, STARTUP_DELAY_MS);
}

/** For tests: stop the pending attempt. */
export function stopBackup(): void {
  if (timer !== null) clearTimeout(timer);
  timer = null;
}

/** Whether care mode should show the failure prominently. */
export function shouldEscalate(status: Pick<BackupStatus, 'failures'>): boolean {
  return status.failures >= ESCALATE_AFTER_FAILURES;
}
